"use client";

import { useEffect } from "react";
import Link from "next/link";
import { Button } from "@/components/ui/button";

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  // Log the error to an error reporting service or console
  useEffect(() => {
    console.error("Application Error:", error);
  }, [error]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-linear-to-br from-background via-background to-destructive/5 p-4">
      <div className="max-w-lg w-full mx-auto text-center space-y-8 animate-fade-in-up">
        
        {/* Animated Error Visual with floating elements */}
        <div className="relative flex justify-center mb-8">
          <div className="relative group">
            {/* Background glow effect - utilizing destructive/red tone for error context */}
            <div className="absolute inset-0 bg-destructive/10 rounded-full blur-3xl scale-150 group-hover:bg-destructive/20 transition-all duration-500"></div>

            {/* Main Error Text */}
            <div className="relative text-8xl font-bold bg-linear-to-br from-destructive via-destructive/80 to-destructive/60 bg-clip-text text-transparent select-none tracking-tighter">
              500
            </div>

            {/* Floating decorative elements */}
            <div className="absolute -top-4 -right-4 w-3 h-3 bg-destructive/40 rounded-full animate-bounce delay-100"></div>
            <div className="absolute -bottom-2 -left-6 w-2 h-2 bg-destructive/60 rounded-full animate-bounce delay-300"></div>
            <div className="absolute top-1/2 -right-8 w-1.5 h-1.5 bg-destructive/50 rounded-full animate-bounce delay-500"></div>
          </div>
        </div>

        {/* Enhanced error message with icon */}
        <div className="space-y-4">
          <div className="flex items-center justify-center space-x-3">
            <div className="p-3 bg-muted/50 rounded-full">
              <svg
                className="h-6 w-6 text-destructive/80"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
          </div>

          <h1 className="text-3xl font-bold text-foreground">
            Something went wrong!
          </h1>

          <div className="max-w-md mx-auto space-y-2">
            <p className="text-muted-foreground text-lg leading-relaxed">
              We encountered an unexpected issue processing your request.
            </p>
            {/* Optional: Display specific error message in dev mode or subtly */}
              <p className="text-xs text-muted-foreground/60 font-mono bg-muted/30 p-2 rounded">
                Error {error.message || "Unknown error occurred"}
              </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
          {/* Primary Action: Reset/Retry */}
          <Button
            onClick={reset}
            size="lg"
            className="group relative overflow-hidden bg-primary hover:bg-primary/90 transition-all duration-300 transform hover:scale-105 hover:shadow-lg"
          >
            <div className="flex items-center space-x-2">
              <svg
                className="w-4 h-4 transition-transform group-hover:rotate-180 duration-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              <span>Try Again</span>
            </div>
          </Button>

          {/* Secondary Action: Go Home */}
          <Button
            variant="outline"
            asChild
            size="lg"
            className="group border-2 hover:border-primary/50 transition-all duration-300 transform hover:scale-105"
          >
            <Link href="/" className="flex items-center space-x-2">
              <svg
                className="w-4 h-4 transition-transform group-hover:-translate-x-1"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
              <span>Back to Home</span>
            </Link>
          </Button>
        </div>
      </div>

      {/* Reusing the animation styles */}
      <style jsx>{`
        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-in-up {
          animation: fade-in-up 0.8s ease-out;
        }
      `}</style>
    </div>
  );
}